import React, { useEffect, useState } from 'react';
import { API_URL } from '../../api-config';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Registrar los módulos de Chart.js (dejamos ChartDataLabels registrado, pero lo desactivaremos en las opciones)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

function getWebSocketURL(apiURL) {
  const isSecure = apiURL.startsWith('https://');
  const wsProtocol = isSecure ? 'wss' : 'ws';
  const wsURL = apiURL.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsURL}/trading-api/historical/ws`;
}

const VolumenPorNiveles = () => {
  const [ticks, setTicks] = useState([]);
  const [filter, setFilter] = useState('last_day'); // Filtro activo
  const [datosChart, setDatosChart] = useState({
    labels: [],
    volumenT: [],
    volumenP: [],
  });

  // 1. Función para traer los últimos 2000 ticks
  const getLastTicks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/trading-api/historical/ticks/2000`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTicks(response.data);
    } catch (error) {
      console.error('Error al obtener ticks:', error);
    }
  };

  // 2. Filtrar según intervalo de tiempo
  const filtrarTicksPorTiempo = (allTicks) => {
    const ahora = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' })
    );

    return allTicks.filter((tick) => {
      const tickDateTime = new Date(tick.Datetime);
      switch (filter) {
        case 'last_5_minutes':
          return ahora - tickDateTime <= 5 * 60 * 1000;
        case 'last_15_minutes':
          return ahora - tickDateTime <= 15 * 60 * 1000;
        case 'last_half_hour':
          return ahora - tickDateTime <= 30 * 60 * 1000;
        case 'last_hour':
          return ahora - tickDateTime <= 60 * 60 * 1000;
        case 'last_2_hours':
          return ahora - tickDateTime <= 2 * 60 * 60 * 1000;
        case 'last_24_hours':
          return ahora - tickDateTime <= 24 * 60 * 60 * 1000;
        case 'last_day':
        default:
          return (
            tick.Fecha ===
            ahora.toLocaleDateString('en-US', { timeZone: 'America/Santiago' })
          );
      }
    });
  };

  // 3. Calcular volúmenes por nivel de precio, agrupando con 1 decimal
  const calcularVolumenPorNivel = (allTicks) => {
    const filtered = filtrarTicksPorTiempo(allTicks);

    // Orden cronológico ascendente
    const sorted = [...filtered].sort(
      (a, b) => new Date(a.Datetime) - new Date(b.Datetime)
    );

    // { nivel: { T: sumaVolumenT, P: sumaVolumenP } }
    const volumenByLevel = {};
    let prevTick = null;

    sorted.forEach((tick) => {
      // 3.1 Etiquetar T o P
      let etiqueta = 'T';
      if (prevTick) {
        const diff = tick.Precio - prevTick.Precio;
        etiqueta = diff > 0 ? 'T' : diff < 0 ? 'P' : etiqueta;
      }
      prevTick = tick;

      // 3.2 Redondeo a 1 decimal
      const nivel = parseFloat(tick.Precio.toFixed(1));

      // 3.3 Inicializar si no existe
      if (!volumenByLevel[nivel]) {
        volumenByLevel[nivel] = { T: 0, P: 0 };
      }
      // 3.4 Sumar volumen
      volumenByLevel[nivel][etiqueta] += tick.Volumen;
    });

    // 4. Preparar arreglos para Chart.js (labels y datos)
    const nivelesOrdenados = Object.keys(volumenByLevel)
      .map((lvl) => parseFloat(lvl))
      .sort((a, b) => b - a)
      .map((num) => num.toFixed(1));

    const volumenT = nivelesOrdenados.map(
      (lvl) => volumenByLevel[parseFloat(lvl)].T
    );
    const volumenP = nivelesOrdenados.map(
      (lvl) => volumenByLevel[parseFloat(lvl)].P
    );

    setDatosChart({
      labels: nivelesOrdenados,
      volumenT,
      volumenP,
    });
  };

  // 5. Carga inicial y WebSocket
  useEffect(() => {
    getLastTicks();

    const wsURL = getWebSocketURL(API_URL);
    const socket = new WebSocket(wsURL);

    socket.onopen = () => {
      console.log('WebSocket conectado (VolumenPorNiveles)');
    };

    socket.onmessage = (event) => {
      try {
        const incoming = JSON.parse(event.data);
        if (incoming.type === 'ping') return;

        const nuevaEntrada = {
          Fecha: incoming.datetime.split(' ')[0],
          Precio: incoming.price,
          Volumen: incoming.volume,
          Datetime: incoming.datetime.replace(' ', 'T'),
        };
        setTicks((prev) => [nuevaEntrada, ...prev]);
      } catch (err) {
        console.error('Error WebSocket (niveles):', err);
      }
    };

    socket.onclose = () => {
      console.log('WebSocket desconectado (VolumenPorNiveles)');
    };
    socket.onerror = (err) => {
      console.error('WebSocket error (niveles):', err);
    };

    return () => {
      socket.close();
    };
  }, []);

  // 6. Recalcular cada vez que cambian ticks o filtro
  useEffect(() => {
    if (ticks.length) {
      calcularVolumenPorNivel(ticks);
    }
  }, [ticks, filter]);

  // 7. Datos para el gráfico
  const barData = {
    labels: datosChart.labels,
    datasets: [
      {
        label: 'T (Volumen ↑)',
        data: datosChart.volumenT,
        backgroundColor: '#34d399',
      },
      {
        label: 'P (Volumen ↓)',
        data: datosChart.volumenP,
        backgroundColor: '#f87171',
      },
    ],
  };

  const barOptions = {
    indexAxis: 'y', // gráfico horizontal
    responsive: true,
    maintainAspectRatio: false, // permitir altura dinámica
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Volumen acumulado',
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Precio (nivel)',
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Volumen por Nivel de Precio (T vs P)',
      },
      datalabels: {
        display: false, // <-- Desactivar etiquetas fijas sobre barras
      },
      tooltip: {
        enabled: true, // Tooltip por defecto en hover
      },
    },
  };

  // Array de filtros para renderizar botones
  const filtrosDisponibles = [
    ['last_5_minutes', 'Últimos 5 minutos'],
    ['last_15_minutes', 'Últimos 15 minutos'],
    ['last_half_hour', 'Última media hora'],
    ['last_hour', 'Última hora'],
    ['last_24_hours', 'Últimas 24 horas'],
    ['last_day', 'Último día'],
  ];

  return (
    <div className="min-h-screen bg-[#101523] flex flex-col items-center px-6 py-4">
      {/* Encabezado */}
      <div className="w-full max-w-lg bg-gray-800 text-white p-4 mb-4 rounded-lg">
        <h2 className="text-lg font-bold">Volumen por Niveles de Precio</h2>
        <p className="text-sm">
          Filtrando ticks de: <span className="font-semibold">{filter}</span>
        </p>
      </div>

      {/* Botones de filtro */}
      <div className="mb-6">
        {filtrosDisponibles.map(([value, label]) => (
          <button
            key={value}
            className={`mx-2 px-4 py-2 rounded ${
              filter === value ? 'bg-blue-700' : 'bg-blue-500'
            } text-white`}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Contenedor del gráfico con altura fija */}
      <div className="w-full max-w-md mb-6 h-96">
        {/* h-96 = 24rem (~384px). Puedes ajustar según necesites */}
        <Bar data={barData} options={barOptions} />
      </div>
    </div>
  );
};

export default VolumenPorNiveles;
